import { forwardRef } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Check } from "lucide-react";

const plans = [
  {
    name: "Free",
    price: "₹0",
    period: "forever",
    description: "Perfect for trying Krew with your squad.",
    features: [
      "Create unlimited Krews",
      "Group coordination & voting",
      "Basic venue recommendations",
      "Auto-split settlements",
      "₹500 commitment deposit",
    ],
    cta: "Start Free",
    featured: false,
  },
  {
    name: "Premium",
    price: "₹99",
    period: "/month",
    description: "For the regulars who krew every weekend.",
    features: [
      "Everything in Free",
      "No commitment deposit (you're verified)",
      "Priority booking at 200+ venues",
      "Exclusive venue access & events",
      "Advanced AI venue matching",
      "Post-night photo albums",
    ],
    cta: "Go Premium",
    featured: true,
  },
];

const PricingSection = forwardRef<HTMLElement>((_, ref) => {
  return (
    <section ref={ref} id="pricing" className="section-padding bg-background">
      <div className="mx-auto max-w-5xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-16 text-center"
        >
          <span className="mb-4 inline-block text-sm font-semibold uppercase tracking-widest text-primary">
            Pricing
          </span>
          <h2 className="text-4xl font-bold md:text-5xl">
            Simple. <span className="text-gradient-neon">Transparent.</span>
          </h2>
        </motion.div>

        <div className="grid gap-8 md:grid-cols-2">
          {plans.map((plan, i) => (
            <motion.div
              key={plan.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className={`relative rounded-2xl border p-8 ${
                plan.featured
                  ? "border-primary/50 bg-card glow-neon"
                  : "border-border bg-card"
              }`}
            >
              {plan.featured && (
                <span className="absolute -top-3 left-6 rounded-full bg-primary px-4 py-1 text-xs font-semibold text-primary-foreground">
                  Most Popular
                </span>
              )}
              <h3 className="text-lg font-semibold text-foreground">{plan.name}</h3>
              <div className="mt-4 flex items-baseline gap-1">
                <span className="text-5xl font-bold text-foreground">{plan.price}</span>
                <span className="text-sm text-muted-foreground">{plan.period}</span>
              </div>
              <p className="mt-3 text-sm text-muted-foreground font-body">{plan.description}</p>

              <ul className="mt-8 space-y-3">
                {plan.features.map((feature) => (
                  <li key={feature} className="flex items-center gap-3">
                    <Check className="h-4 w-4 text-primary" />
                    <span className="text-sm text-foreground font-body">{feature}</span>
                  </li>
                ))}
              </ul>

              <Link
                to={plan.featured ? "/auth?redirect=/dashboard" : "/auth"}
                className={`mt-8 block w-full rounded-xl py-3.5 text-center text-sm font-semibold transition-all ${
                  plan.featured
                    ? "bg-primary text-primary-foreground hover:opacity-90"
                    : "border border-border bg-muted text-foreground hover:border-primary/40"
                }`}
              >
                {plan.featured ? "Go Premium (Coming Soon)" : plan.cta}
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
});

PricingSection.displayName = "PricingSection";

export default PricingSection;
